import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Reminder } from 'types/entities/Reminder'
import { ColumnFiltersState, SortingState } from '@tanstack/react-table'

type TableSearchScalar = string | number | boolean | null
type TableSearch =
    | TableSearchScalar
    | TableSearchScalar[]
    | Record<string, TableSearchScalar | TableSearchScalar[]>

interface RemindersTableUiState {
    pageIndex: number
    pageSize: number
    globalFilter: string
    columnFilters: ColumnFiltersState
    sorting: SortingState
    view: string
}

interface RemindersTableMetaState {
    currentPage: number
    perPage: number
    total: number
    search: TableSearch
}

export interface RemindersState {
    reminders: Reminder[]
    selectedReminder: Reminder | null
    table: {
        ui: RemindersTableUiState
        meta: RemindersTableMetaState | null
    }
}

const initialState: RemindersState = {
    reminders: [],
    selectedReminder: null,
    table: {
        ui: {
            pageIndex: 0,
            pageSize: 50,
            globalFilter: '',
            columnFilters: [],
            sorting: [{ id: 'id', desc: true }],
            view: 'all',
        },
        meta: null,
    },
}

const remindersSlice = createSlice({
    name: 'reminders',
    initialState,
    reducers: {
        setReminders(state, action: PayloadAction<Reminder[]>) {
            state.reminders = action.payload
        },
        pushReminder: {
            prepare: (payload: Reminder, created = false) => ({payload, meta: {created}}),
            reducer(state, action: PayloadAction<Reminder, string, {created: boolean}>) {
                const index = state.reminders.findIndex(
                    (r) => r.id === action.payload.id
                )
                if (index !== -1) {
                    state.reminders[index] = action.payload
                } else {
                    state.reminders.push(action.payload)
                    if (action.meta.created && state.table.meta) state.table.meta.total += 1
                }
            },
        },
        deleteReminder(state, action: PayloadAction<number>) {
            state.reminders = state.reminders.filter(
                (r) => r.id !== action.payload
            )
        },
        setSelectedReminder(state, action: PayloadAction<Reminder | null>) {
            state.selectedReminder = action.payload
        },
        setRemindersTableUiState(
            state,
            action: PayloadAction<Partial<RemindersTableUiState>>
        ) {
            if (!state.table) {
                state.table = {
                    ui: { ...initialState.table.ui },
                    meta: null,
                }
            }
            state.table.ui = { ...state.table.ui, ...action.payload }
        },
        setRemindersTableMeta(
            state,
            action: PayloadAction<RemindersTableMetaState>
        ) {
            if (!state.table) {
                state.table = {
                    ui: { ...initialState.table.ui },
                    meta: null,
                }
            }
            state.table.meta = action.payload
        },
        resetState: () => initialState,
    },
})

export const {
    setReminders,
    pushReminder,
    deleteReminder,
    setSelectedReminder,
    setRemindersTableUiState,
    setRemindersTableMeta,
    resetState,
} = remindersSlice.actions

export default remindersSlice.reducer
